// import { Navbar } from "../components/layout/Navbar";
// import { Footer } from "../components/layout/Footer";
import { Button } from "../components/ui/button";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../components/ui/card";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "../components/ui/accordion";
import { CheckCircle2, Shield, Zap, Users, Coins, FileText, ArrowRight, ArrowLeft } from "lucide-react";
import { motion } from "framer-motion";

interface CompanyFormationCorpnetLandingProps {
  showNavbar?: boolean;
  showFooter?: boolean;
  onNavigate?: (page: string) => void;
}

export default function CompanyFormationCorpnetLanding({ showNavbar = false, showFooter = false, onNavigate }: CompanyFormationCorpnetLandingProps) {
  const fadeIn = {
    initial: { opacity: 0, y: 20 },
    whileInView: { opacity: 1, y: 0 },
    viewport: { once: true },
    transition: { duration: 0.5 }
  };

  const advantages = [
    {
      title: "Simplicity",
      icon: Zap,
      desc: "Next to operating a business as a Sole Proprietor or Partnership, Limited Liability Companies are the least complex and costly form of business to start and maintain from a state compliance perspective. The business registration paperwork to register an LLC is minimal as are the ongoing filing requirements."
    },
    {
      title: "Personal Liability Protection",
      icon: Shield,
      desc: "Because an LLC is considered a separate legal entity from its members, its financial and legal responsibilities are also its own. So, if someone sues the business or the company cannot pay its debts, the LLC members are typically not held responsible. Therefore, their personal assets are at a lower risk of being seized to pay legal damages or settle debt than they would be if the business were a Sole Proprietorship or Partnership."
    },
    {
      title: "Tax Treatment Options",
      icon: Coins,
      desc: "By default, a Single Member LLC is considered a \"disregarded entity\" for tax purposes. As such, income tax is applied in the same way as it is to Sole Proprietorships. Multi-Member LLCs are taxed as Partnerships. Business income and losses are passed through to an LLC members' tax returns and are subject to the individuals' income tax rates and self-employment taxes (Social Security and Medicare). An LLC has other tax treatment options, too. Members can opt for an LLC to be taxed as a corporation, with profits taxed at its corporate rate. Or members of an LLC can choose S Corporation election, which allows for the LLC to have pass-through taxation but with the corporate benefit of a reduced self-employment tax burden because members only pay Social Security and Medicare taxes on their income taken as salaries; members' dividend income is not subject to self-employment taxes."
    },
    {
      title: "Management Flexibility",
      icon: Users,
      desc: "An LLC may be either member-managed or manager-managed. In a member-managed LLC, the owners handle the day-to-day management of the business. In a manager-managed LLC, members appoint one or more managers to manage the company. In most states, an LLC can appoint members of the LLC to be managers, or it can hire someone else to do the job. The managers of an LLC usually have the authority to make certain decisions and run the day-to-day operations of the company while members retain authority over more significant strategic matters. Most states consider an LLC to be member-managed unless the formation paperwork indicates it should be manager-managed."
    }
  ];

  const llcTypes = [
    {
      title: "Single Member LLC",
      desc: "If a company will have just one owner or a married couple as the owner, it is considered a single-member LLC."
    },
    {
      title: "Multi-Member LLC",
      desc: "If an LLC will have more than one member, it is considered a multi-member LLC."
    },
    {
      title: "Member Managed LLC",
      desc: "When owners run the day-in, day-out operations of the business, the LLC is member-managed. Most LLCs use this structure."
    },
    {
      title: "Manager-Managed LLC",
      desc: "When LLC members want to appoint a manager (who could be someone that the company hires or one of the members) to handle daily operations, the LLC is manager-managed."
    },
    {
      title: "Domestic LLC",
      desc: "When an LLC has registered its formation documents (Articles of Organization) in a state, it goes on record as a domestic LLC there. That state is the company's domicile (a.k.a. home state)."
    },
    {
      title: "Foreign LLC",
      desc: "If an LLC is registered as a domestic LLC in one state and is conducting business in another state (physical presence or economic Nexus), it must usually file as a foreign LLC in the additional state(s)."
    },
    {
      title: "PLLC",
      desc: "A Professional Limited Liability Company (PLLC) differs from the standard LLC in that only professional license holders (physician, attorney, accountant, etc.) may register the company. Like a regular LLC, a PLLC may elect to be treated as an S Corporation for tax purposes, which can help minimize the self-employment tax burden on its members."
    },
    {
      title: "Series LLC",
      desc: "Series LLCs are business entities (available in some states but not all) that have a parent (umbrella) LLC and other LLCs (series) beneath it with their own debts, obligations, and rights. Usually, individual series in the series LLC are taxed separately."
    }
  ];

  const faqItems = [
    {
      question: "Can One Person Form an LLC?",
      answer: "Yes, one person can form an LLC. This is called a Single Member LLC. A Single Member LLC is treated as a disregarded entity for tax purposes, meaning the IRS treats it as a sole proprietorship for tax purposes, but the owner still receives the liability protection benefits of an LLC."
    },
    {
      question: "What Is the LLC Organizer?",
      answer: "The LLC organizer is the person or entity responsible for filing the Articles of Organization with the state. The organizer can be a member of the LLC, a non-member, or a professional filing service like CorpNet. The organizer's role is typically complete once the LLC is formed."
    },
    {
      question: "What Is an LLC Member?",
      answer: "An LLC member is an owner of the Limited Liability Company. Members can be individuals, other LLCs, corporations, or other business entities. Members have an ownership interest in the LLC and typically share in the profits and losses of the business."
    },
    {
      question: "What Is an LLC Manager?",
      answer: "An LLC manager is a person appointed to manage the day-to-day operations of a manager-managed LLC. Managers can be members of the LLC or non-members hired to run the business. In a member-managed LLC, all members participate in management decisions."
    },
    {
      question: "Does an LLC Have Stockholders?",
      answer: "No, LLCs do not have stockholders. Instead, they have members who hold membership interests. Unlike corporations, LLCs do not issue stock. Ownership is represented by membership interests or membership units."
    },
    {
      question: "How Is an LLC Structured?",
      answer: "An LLC is structured with members (owners) and can be either member-managed or manager-managed. The structure is outlined in the Operating Agreement, which defines ownership percentages, management structure, profit and loss distribution, and operating procedures."
    },
    {
      question: "What are Membership Interests in an LLC?",
      answer: "Membership interests represent ownership in an LLC, similar to stock in a corporation. They define each member's percentage of ownership, rights to profits and losses, and voting rights. Membership interests are typically outlined in the Operating Agreement."
    },
    {
      question: "What Is a Member-Managed LLC?",
      answer: "A member-managed LLC is one where all members participate in the day-to-day management and decision-making of the business. This is the default structure in most states unless the formation documents specify otherwise."
    },
    {
      question: "What Is a Manager-Managed LLC?",
      answer: "A manager-managed LLC is one where members appoint one or more managers (who may or may not be members) to handle the daily operations and certain business decisions. Members retain authority over significant strategic matters."
    },
    {
      question: "What Are the Tax Advantages of the LLC Structure?",
      answer: "LLCs offer pass-through taxation by default, meaning business income and losses are reported on members' personal tax returns, avoiding double taxation. LLCs can also elect to be taxed as an S-Corp or C-Corp if beneficial. Additionally, members can deduct business losses against other income, and there's flexibility in profit and loss allocation."
    },
    {
      question: "What Are the Potential Tax Disadvantages of the LLC Structure?",
      answer: "LLC members are subject to self-employment taxes on all business income. Some states impose franchise taxes or annual fees on LLCs. Additionally, members cannot take advantage of certain corporate tax benefits like retained earnings at lower tax rates, and there may be limitations on employee benefits compared to corporations."
    },
    {
      question: "What Is the Difference Between How LLCs and S Corporations Are Taxed?",
      answer: "Both LLCs and S Corporations offer pass-through taxation, but there are key differences. S Corporations allow owners to take a reasonable salary (subject to payroll taxes) and receive remaining profits as distributions (not subject to self-employment tax). LLCs can elect S-Corp taxation to achieve similar benefits, but by default, all LLC income is subject to self-employment taxes."
    },
    {
      question: "What Is the LLC Organizational Meeting?",
      answer: "The LLC organizational meeting is the initial meeting where members adopt the Operating Agreement, issue membership interests, and make other foundational decisions. While not always required by state law, it's a best practice to document these decisions."
    },
    {
      question: "What Is an LLC Operating Agreement?",
      answer: "An LLC Operating Agreement is a legal document that outlines the ownership structure, management procedures, profit and loss distribution, voting rights, and operating procedures of the LLC. While not always required by state law, it's highly recommended to prevent disputes and clarify member expectations."
    },
    {
      question: "Do You Need an Operating Agreement to Start an LLC?",
      answer: "Most states do not require an Operating Agreement to form an LLC, but it's strongly recommended. Without one, your LLC will be governed by default state laws, which may not align with your business needs. An Operating Agreement provides clarity and protection for all members."
    },
    {
      question: "Does an LLC Need to Hold an Annual Meeting?",
      answer: "Unlike corporations, LLCs are generally not required to hold annual meetings or keep formal meeting minutes. However, it's still a good practice to document major decisions and maintain records of important business activities."
    },
    {
      question: "What's the Difference Between an LLC and PLLC?",
      answer: "A PLLC (Professional Limited Liability Company) is similar to an LLC but is restricted to licensed professionals such as doctors, lawyers, accountants, and architects. Only licensed professionals in the relevant field can be members of a PLLC, and it provides the same liability protection and tax benefits as a standard LLC."
    },
    {
      question: "What Is a Registered Agent?",
      answer: "A registered agent is a person or business entity designated to receive official legal documents, government notices, and service of process on behalf of the LLC. The registered agent must have a physical address in the state where the LLC is formed and be available during normal business hours."
    },
    {
      question: "If an LLC Operates in Multiple States, Does It Need a Registered Agent in Each State?",
      answer: "Yes, if an LLC conducts business in multiple states (through foreign qualification), it must appoint a registered agent in each state where it's registered. Each state requires its own registered agent with a physical address in that state."
    },
    {
      question: "What Happens if an LLC Does Not Have a Registered Agent?",
      answer: "If an LLC fails to maintain a registered agent, it may face penalties, lose its good standing with the state, and could be administratively dissolved. Additionally, the LLC may miss important legal documents or notices, which could result in default judgments or other legal issues."
    },
    {
      question: "What Is an Initial Report and Does an LLC Need to File One?",
      answer: "An initial report (also called an initial statement or annual report) is a filing required by some states shortly after forming an LLC. It typically includes updated information about the LLC's members, managers, and registered agent. Not all states require initial reports, and requirements vary by state."
    },
    {
      question: "What Is an LLC Annual Report Filing?",
      answer: "An annual report (or biennial report in some states) is a periodic filing required to keep the LLC's information current with the Secretary of State. It typically includes updated member/manager information, business address, and registered agent details. Filing requirements and deadlines vary by state."
    },
    {
      question: "Which Is the Best State for Forming an LLC?",
      answer: "The best state depends on your specific situation. Many choose their home state for simplicity. Delaware is popular for its business-friendly laws and well-developed corporate statutes. Nevada and Wyoming are attractive for their low fees and tax benefits. Consider where you'll primarily conduct business, as you may need to foreign qualify in your home state anyway."
    },
    {
      question: "When Is the Best Time to Register an LLC?",
      answer: "The best time to register an LLC is before you start conducting business or accepting payments. Forming early protects your personal assets from the start and allows you to establish business credit, open business bank accounts, and enter into contracts in the company's name."
    },
    {
      question: "Can the Advantages of an LLC Happen Retroactively?",
      answer: "No, LLC advantages like liability protection and tax benefits only apply from the date of formation forward. You cannot retroactively protect past business activities or change how past income was taxed. This is why it's important to form your LLC before starting business operations."
    },
    {
      question: "How Can I Keep My LLC Compliant?",
      answer: "To keep your LLC compliant, you must: maintain a registered agent, file required annual/biennial reports on time, keep your Operating Agreement updated, maintain separate business and personal finances, obtain necessary business licenses, file required tax returns, and keep accurate business records. Consider using a compliance service to track deadlines."
    },
    {
      question: "How Can I Create an LLC?",
      answer: "To create an LLC, you typically need to: choose a business name and ensure it's available, file Articles of Organization with your state, appoint a registered agent, create an Operating Agreement, obtain an EIN from the IRS, open a business bank account, and obtain necessary business licenses. You can do this yourself or use a professional filing service like CorpNet to handle the paperwork."
    }
  ];

  const allStates = [
    "Alabama", "Alaska", "Arizona", "Arkansas", "California", "Colorado", "Connecticut", "Delaware",
    "Florida", "Georgia", "Hawaii", "Idaho", "Illinois", "Indiana", "Iowa", "Kansas", "Kentucky",
    "Louisiana", "Maine", "Maryland", "Massachusetts", "Michigan", "Minnesota", "Mississippi", "Missouri",
    "Montana", "Nebraska", "Nevada", "New Hampshire", "New Jersey", "New Mexico", "New York", "North Carolina",
    "North Dakota", "Ohio", "Oklahoma", "Oregon", "Pennsylvania", "Rhode Island", "South Carolina", "South Dakota",
    "Tennessee", "Texas", "Utah", "Vermont", "Virginia", "Washington", "West Virginia", "Wisconsin", "Wyoming"
  ];

  const maintenanceItems = [
    { title: "Registered Agent", desc: "Every LLC must appoint a registered agent to receive official legal documents and government notices on behalf of the business." },
    { title: "Annual Reports", desc: "Most states require LLCs to file an annual or biennial report to keep the business's information current with the Secretary of State." },
    { title: "Operating Agreement", desc: "While not always required by state law, this document outlines the ownership and operating procedures of the LLC to prevent future disputes." },
    { title: "Federal Tax ID (EIN)", desc: "Essential for opening a business bank account, hiring employees, and filing business taxes with the IRS." }
  ];

  return (
    <div className="min-h-screen bg-background flex flex-col font-sans">
      {/* {showNavbar && <Navbar onNavigate={onNavigate} />} */}
      
      {/* Back Button */}
      
      <main className="flex-grow">
        {/* Hero Section */}
        <section className="bg-primary text-white py-24 relative overflow-hidden">
        <div style={{ position: 'absolute', top: '0.5rem', left: '0.5rem' }}>
          <Button
            variant="outline"
            size="sm"
            onClick={() => onNavigate?.('home')}
            className="bg-white/90 backdrop-blur-sm hover:bg-white shadow-md border-gray-200 text-black"
          >
            <ArrowLeft className="h-4 w-4 mr-2" />
            Back to Home
          </Button>
        </div>
          <div className="container mx-auto px-4 md:px-6 relative z-10 text-center max-w-4xl">
            <motion.div {...fadeIn}>
              <h1 className="text-4xl md:text-6xl font-bold font-heading mb-6">
                Limited Liability Company <br/>
                <span className="text-secondary">Form an LLC With CorpNet</span>
              </h1>
              <p className="text-xl text-blue-100 mb-10 leading-relaxed">
                Forming an LLC is simple with CorpNet. Our online application offers upfront pricing and no unwanted upsells. 
                Registering your new Limited Liability Company with CorpNet is quick and your satisfaction is guaranteed.
              </p>
              <div className="mb-12">
                <h2 className="text-2xl md:text-3xl font-bold font-heading mb-4">Ready to get started?</h2>
                <p className="text-lg text-blue-100 mb-6">Register your business today! CorpNet makes it fast and easy.</p>
              </div>
              <div className="flex flex-col sm:flex-row justify-center gap-4">
                <Button size="lg" className="bg-secondary hover:bg-secondary/90 text-white font-bold h-14 px-10 text-lg shadow-lg shadow-blue-500/20">
                  Order Now
                </Button>
                <Button size="lg" variant="outline" className="text-white border-white/30 hover:bg-white/10 h-14 px-10 text-lg">
                  Talk to an Expert
                </Button>
              </div>
            </motion.div>
          </div>
        </section>

        {/* Video Placeholder/Context Section */}
        <section className="py-20 bg-gradient-to-b from-white via-blue-50/30 to-white">
          <div className="container mx-auto px-4 md:px-6">
            <motion.div 
              {...fadeIn}
              className="max-w-6xl mx-auto"
            >
              <div className="bg-white rounded-3xl overflow-hidden shadow-xl border border-gray-100/50 flex flex-col lg:flex-row items-stretch">
                <div className="w-full lg:w-1/2 aspect-video lg:aspect-auto bg-gradient-to-br from-primary/90 to-primary flex items-center justify-center relative group cursor-pointer overflow-hidden">
                  <div className="absolute inset-0 bg-black/20 group-hover:bg-black/10 transition-all duration-300"></div>
                  <div className="absolute inset-0 bg-gradient-to-t from-black/40 via-transparent to-transparent"></div>
                  <div className="w-20 h-20 bg-white/95 backdrop-blur-sm rounded-full flex items-center justify-center text-primary shadow-2xl z-10 transform group-hover:scale-110 transition-all duration-300">
                    <div className="w-0 h-0 border-t-[12px] border-t-transparent border-l-[20px] border-l-current border-b-[12px] border-b-transparent ml-1"></div>
                  </div>
                  <span className="absolute bottom-6 left-6 text-white text-sm font-semibold bg-black/60 backdrop-blur-sm px-4 py-2 rounded-full border border-white/20">
                    Watch Video: What Is an LLC?
                  </span>
                </div>
                <div className="w-full lg:w-1/2 p-10 lg:p-12 flex flex-col justify-center bg-gradient-to-br from-white to-blue-50/20">
                  <h2 className="text-3xl lg:text-4xl font-bold font-heading text-primary mb-6 leading-tight">
                    Why Should You Register an LLC?
                  </h2>
                  <p className="text-muted-foreground leading-relaxed text-lg">
                    A Limited Liability Company (LLC) is a formal business structure that is the simplest to form and maintain. Creating an LLC offers some of the same benefits as a C Corporation, without the costs and compliance complexity. Business owners who are looking for personal liability protection, tax flexibility, and management options may find that registering an LLC will be an ideal choice for their company.
                  </p>
                </div>
              </div>
            </motion.div>
          </div>
        </section>

        {/* Advantages Section */}
        <section className="py-28 bg-white">
          <div className="container mx-auto px-4 md:px-6">
            <motion.div {...fadeIn} className="text-center max-w-3xl mx-auto mb-20">
              <h2 className="text-4xl md:text-5xl lg:text-6xl font-bold font-heading text-primary mb-6 leading-tight">
                Four Advantages of an LLC Business Structure
              </h2>
              <div className="w-24 h-1 bg-gradient-to-r from-secondary to-primary mx-auto rounded-full"></div>
            </motion.div>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8 lg:gap-10 max-w-7xl mx-auto">
              {advantages.map((adv, i) => (
                <motion.div 
                  key={i} 
                  {...fadeIn} 
                  transition={{ delay: i * 0.1 }}
                  className="group relative"
                >
                  <div className="h-full flex gap-6 p-8 lg:p-10 rounded-2xl bg-gradient-to-br from-white to-blue-50/30 border border-gray-100/80 shadow-sm hover:shadow-xl transition-all duration-300 hover:-translate-y-1">
                    <div className="shrink-0 w-16 h-16 lg:w-20 lg:h-20 rounded-2xl bg-gradient-to-br from-primary/10 to-secondary/10 flex items-center justify-center text-secondary group-hover:scale-110 transition-transform duration-300">
                      <adv.icon className="h-8 w-8 lg:h-10 lg:w-10" />
                    </div>
                    <div className="flex-1">
                      <h3 className="text-2xl font-bold font-heading text-primary mb-4 group-hover:text-secondary transition-colors">
                        {adv.title}
                      </h3>
                      <p className="text-muted-foreground leading-relaxed text-base lg:text-lg">
                        {adv.desc}
                      </p>
                    </div>
                  </div>
                </motion.div>
              ))}
            </div>
          </div>
        </section>

        {/* Types of LLCs Section */}
        <section className="py-28 bg-gradient-to-b from-white via-blue-50/40 to-white">
          <div className="container mx-auto px-4 md:px-6">
            <div className="max-w-7xl mx-auto">
              <motion.div {...fadeIn} className="text-center mb-20">
                <h2 className="text-4xl md:text-5xl lg:text-6xl font-bold font-heading text-primary mb-6 leading-tight">
                  Types of LLCs
                </h2>
                <div className="w-24 h-1 bg-gradient-to-r from-secondary to-primary mx-auto rounded-full mb-6"></div>
                <p className="text-lg lg:text-xl text-muted-foreground max-w-4xl mx-auto leading-relaxed">
                  There are multiple types of Limited Liability Companies available. Before you set up your Limited Liability Company, 
                  it is best to consider your needs and then compare these requirements to the available LLC options. Below is a list of 
                  available LLC options, however, not all states recognize all types of LLCs, so entrepreneurs must review their state's options.
                </p>
              </motion.div>
              
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
                {llcTypes.map((type, i) => (
                  <motion.div
                    key={i}
                    {...fadeIn}
                    transition={{ delay: i * 0.05 }}
                    className="h-full"
                  >
                    <Card className="h-full bg-white/80 backdrop-blur-sm border-gray-100/80 shadow-sm hover:shadow-xl transition-all duration-300 hover:-translate-y-1 hover:border-primary/20">
                      <CardHeader className="pb-4">
                        <CardTitle className="text-primary font-heading text-lg lg:text-xl leading-tight">
                          {type.title}
                        </CardTitle>
                      </CardHeader>
                      <CardContent>
                        <p className="text-muted-foreground text-sm lg:text-base leading-relaxed">
                          {type.desc}
                        </p>
                      </CardContent>
                    </Card>
                  </motion.div>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* Requirements Section */}
        <section className="py-28 bg-gradient-to-br from-primary via-primary to-primary/95 text-white relative overflow-hidden">
          <div className="absolute inset-0 bg-[url('data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNjAiIGhlaWdodD0iNjAiIHZpZXdCb3g9IjAgMCA2MCA2MCIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48ZyBmaWxsPSJub25lIiBmaWxsLXJ1bGU9ImV2ZW5vZGQiPjxnIGZpbGw9IiNmZmYiIGZpbGwtb3BhY2l0eT0iMC4wNSI+PHBhdGggZD0iTTM2IDM0djJoLTJ2LTJoMnptMC0yMGgyVjEyaC0ydjJ6bTIwIDIwVjM0aC0ydjJoMnptMC0yMGgyVjEyaC0ydjJ6Ii8+PC9nPjwvZz48L3N2Zz4=')] opacity-20"></div>
          <div className="container mx-auto px-4 md:px-6 relative z-10">
            <div className="max-w-7xl mx-auto">
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 lg:gap-20 items-center">
                <div>
                  <h2 className="text-4xl md:text-5xl lg:text-6xl font-bold font-heading mb-8 leading-tight text-blue-600">
                    Maintain Your LLC Compliance
                  </h2>
                  <p className="text-xl lg:text-2xl text-blue-500 mb-12 leading-relaxed">
                    Forming your LLC is just the first step. To keep your liability protection, you must maintain 
                    your business's standing with the state through several ongoing requirements.
                  </p>
                  <div className="space-y-5 text-black">
                    {[
                      "Appointing a Registered Agent",
                      "Filing Annual or Biennial Reports",
                      "Creating an Operating Agreement",
                      "Obtaining Necessary Business Licenses",
                      "Filing for a Federal Tax ID (EIN)"
                    ].map((item, i) => (
                      <div 
                        key={i} 
                        className="flex items-center gap-4 group"
                      >
                        <div className="bg-secondary rounded-full p-2 shadow-lg group-hover:scale-110 transition-transform duration-300">
                          <CheckCircle2 className="h-5 w-5 text-white" />
                        </div>
                        <span className="text-lg lg:text-xl font-medium text-black">
                          {item}
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
                
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-6 text-black">
                  {maintenanceItems.map((item, i) => (
                    <Card 
                      key={i} 
                      className="bg-white/50 border-white/50 text-white hover:bg-white/60 transition-all duration-300 hover:scale-105 hover:shadow-2xl shadow-xl"
                    >
                      <CardHeader>
                        <CardTitle className="text-blue-500 font-heading text-lg lg:text-xl">
                          {item.title}
                        </CardTitle>
                      </CardHeader>
                      <CardContent>
                        <p className="text-black text-sm lg:text-base leading-relaxed">
                          {item.desc}
                        </p>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* State Selection Section */}
        <section className="py-28 bg-white">
          <div className="container mx-auto px-4 md:px-6">
            <div className="max-w-7xl mx-auto">
              <motion.div {...fadeIn} className="text-center mb-16">
                <h2 className="text-4xl md:text-5xl lg:text-6xl font-bold font-heading text-primary mb-8 leading-tight">
                  Learn More About Starting an LLC in Your Preferred State
                </h2>
                <div className="w-24 h-1 bg-gradient-to-r from-secondary to-primary mx-auto rounded-full mb-12"></div>
                <div className="space-y-6 text-left max-w-5xl mx-auto text-muted-foreground text-lg leading-relaxed">
                  <p>
                    Once you have decided to create an LLC, you must choose the state in which you will be incorporating or registering the LLC. 
                    Generally, if you have a small business and are going to be conducting a substantial amount of your business in your home state, 
                    the general norm is to incorporate it within your home state.
                  </p>
                  <p>
                    Most people choose Delaware, Nevada, or their home state. Delaware is often chosen, especially by larger companies, because it has 
                    the most developed and flexible corporate statutes in the country and is considered pro-business. Nevada has also become popular 
                    because of its lack of state corporate income tax, franchise tax, and personal income tax. It also has relatively low fees.
                  </p>
                  <p>
                    Nevertheless, if you have a small business and are going to be conducting a substantial amount of your business in your home state, 
                    it will likely be beneficial to incorporate it in that state. If you register your LLC out-of-state, but do much of your business in 
                    your home state, you may have to make a filing to "qualify to do business" in the state if there is a substantial ongoing business 
                    or physical presence in the state. You may then be subject to the same fees, taxes, and regulations as if you had incorporated there 
                    in the first place, and you will have paid filing fees (and, perhaps franchise taxes) to more than one state.
                  </p>
                  <p className="font-bold text-primary mt-8 text-xl">
                    Browse through your state options for registering an LLC:
                  </p>
                </div>
              </motion.div>
              
              <motion.div 
                {...fadeIn}
                transition={{ delay: 0.2 }}
                className="flex flex-wrap justify-center gap-3 lg:gap-4"
              >
                {allStates.map((state, i) => (
                  <Button 
                    key={state} 
                    variant="outline" 
                    className="border-gray-200 hover:border-primary hover:bg-primary hover:text-white font-medium transition-all duration-300 hover:scale-105 shadow-sm hover:shadow-md"
                  >
                    Form an LLC in {state}
                  </Button>
                ))}
              </motion.div>
            </div>
          </div>
        </section>

        {/* FAQs Section */}
        <section className="py-28 bg-gradient-to-b from-white via-blue-50/40 to-white">
          <div className="container mx-auto px-4 md:px-6">
            <div className="max-w-5xl mx-auto">
              <motion.div {...fadeIn} className="text-center mb-16">
                <h2 className="text-4xl md:text-5xl lg:text-6xl font-bold font-heading text-primary mb-6 leading-tight">
                  Top LLC FAQs
                </h2>
                <div className="w-24 h-1 bg-gradient-to-r from-secondary to-primary mx-auto rounded-full"></div>
              </motion.div>
              
              <Accordion type="single" collapsible className="w-full space-y-4">
                {faqItems.map((faq, i) => (
                  <motion.div
                    key={i}
                    {...fadeIn}
                    transition={{ delay: i * 0.03 }}
                  >
                    <AccordionItem 
                      value={`item-${i}`} 
                      className="bg-white/80 backdrop-blur-sm rounded-xl border border-gray-100/80 shadow-sm hover:shadow-md transition-all duration-300 overflow-hidden"
                    >
                      <AccordionTrigger className="text-left font-semibold text-primary hover:no-underline py-2 px-6 hover:text-secondary transition-colors">
                        <span className="text-base lg:text-lg pr-4">{faq.question}</span>
                      </AccordionTrigger>
                      <AccordionContent>
                        <div className="text-muted-foreground leading-relaxed pb-4 px-6 text-base lg:text-lg">
                          {faq.answer}
                        </div>
                      </AccordionContent>
                    </AccordionItem>
                  </motion.div>
                ))}
              </Accordion>
            </div>
          </div>
        </section>

        {/* Free Guide Section */}
        <section className="py-28 bg-white">
          <div className="container mx-auto px-4 md:px-6">
            <div className="max-w-7xl mx-auto">
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
                <motion.div {...fadeIn}>
                  <h2 className="text-4xl md:text-5xl font-bold font-heading text-primary mb-8 leading-tight">
                    Is Forming an LLC Right for Your Business?
                  </h2>
                  <div className="space-y-6 text-muted-foreground mb-8">
                    <p className="text-lg lg:text-xl leading-relaxed">
                      Choosing a business entity type for your company has both legal and financial implications. We know there are lots of 
                      questions that come up and we'd like to provide a free resource to help you along your path to entrepreneurship.
                    </p>
                    <p className="text-lg lg:text-xl leading-relaxed">
                      Our free guide to forming an LLC is written by business attorneys at Corpnet.com. This guide answers the common questions 
                      for creating a Limited Liability Company for your new business.
                    </p>
                  </div>
                  <Button 
                    size="lg" 
                    className="bg-secondary hover:bg-secondary/90 text-white font-bold h-14 px-10 text-lg shadow-lg hover:shadow-xl transition-all duration-300 hover:scale-105"
                  >
                    Grab Your Free LLC Guide
                    <ArrowRight className="ml-2 h-5 w-5" />
                  </Button>
                </motion.div>
                <motion.div 
                  {...fadeIn} 
                  transition={{ delay: 0.2 }} 
                  className="hidden lg:block"
                >
                  <div className="bg-gradient-to-br from-blue-50 via-primary/5 to-secondary/10 rounded-3xl p-12 lg:p-16 text-center border border-gray-100/80 shadow-xl hover:shadow-2xl transition-all duration-300">
                    <div className="bg-gradient-to-br from-primary/10 to-secondary/10 rounded-2xl p-6 inline-block mb-6">
                      <FileText className="h-20 w-20 text-secondary mx-auto" />
                    </div>
                    <h3 className="text-3xl font-bold text-primary mb-4">Free LLC Formation Guide</h3>
                    <p className="text-muted-foreground text-lg leading-relaxed">
                      Get expert insights from business attorneys on everything you need to know about forming an LLC.
                    </p>
                  </div>
                </motion.div>
              </div>
            </div>
          </div>
        </section>

        {/* Pricing/Value Section */}
        <section className="py-28 bg-gradient-to-b from-white via-blue-50/40 to-white">
          <div className="container mx-auto px-4 md:px-6">
            <div className="max-w-6xl mx-auto text-center">
              <motion.div {...fadeIn}>
                <div className="inline-flex items-center gap-2 bg-gradient-to-r from-green-50 to-emerald-50 text-green-700 px-6 py-2.5 rounded-full text-sm font-bold mb-10 border border-green-200/50 shadow-sm">
                  <CheckCircle2 className="h-5 w-5" />
                  <span>100% Satisfaction Guaranteed</span>
                </div>
                <h2 className="text-4xl md:text-5xl lg:text-6xl font-bold font-heading text-primary mb-6 leading-tight">
                  100% Satisfaction Guaranteed
                </h2>
                <div className="w-24 h-1 bg-gradient-to-r from-secondary to-primary mx-auto rounded-full mb-8"></div>
                <p className="text-xl lg:text-2xl text-muted-foreground mb-16 leading-relaxed max-w-3xl mx-auto">
                  Or we will refund 100% of our service fees within 60 days of placing your order.
                </p>
                <h3 className="text-3xl md:text-4xl font-bold font-heading text-primary mb-8">
                  Transparency is Our Policy
                </h3>
                <p className="text-xl lg:text-2xl text-muted-foreground mb-16 leading-relaxed max-w-4xl mx-auto">
                  At CorpNet, we believe in upfront pricing. No surprise subscriptions, no unwanted upsells. 
                  Just professional filing services backed by 26 years of legal expertise.
                </p>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-8 lg:gap-10">
                  {[
                    { title: "Expert Review", desc: "Every order is reviewed by our U.S.-based team for accuracy before filing." },
                    { title: "Compliance Alerts", desc: "Receive automated alerts for upcoming filing deadlines to stay in good standing." },
                    { title: "Secure Storage", desc: "Access your filed formation documents 24/7 through our secure client portal." }
                  ].map((item, i) => (
                    <motion.div
                      key={i}
                      {...fadeIn}
                      transition={{ delay: i * 0.1 }}
                      className="bg-white/80 backdrop-blur-sm p-8 lg:p-10 rounded-2xl shadow-sm border border-gray-100/80 hover:shadow-xl transition-all duration-300 hover:-translate-y-1 hover:border-primary/20"
                    >
                      <h3 className="font-bold text-primary mb-4 text-xl">{item.title}</h3>
                      <p className="text-sm lg:text-base text-muted-foreground leading-relaxed">
                        {item.desc}
                      </p>
                    </motion.div>
                  ))}
                </div>
              </motion.div>
            </div>
          </div>
        </section>

        {/* Final CTA */}
        <section className="py-28 bg-gradient-to-br from-primary via-primary to-primary/95 text-white relative overflow-hidden">
          <div className="absolute inset-0 bg-[url('data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNjAiIGhlaWdodD0iNjAiIHZpZXdCb3g9IjAgMCA2MCA2MCIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48ZyBmaWxsPSJub25lIiBmaWxsLXJ1bGU9ImV2ZW5vZGQiPjxnIGZpbGw9IiNmZmYiIGZpbGwtb3BhY2l0eT0iMC4wNSI+PHBhdGggZD0iTTM2IDM0djJoLTJ2LTJoMnptMC0yMGgyVjEyaC0ydjJ6bTIwIDIwVjM0aC0ydjJoMnptMC0yMGgyVjEyaC0ydjJ6Ii8+PC9nPjwvZz48L3N2Zz4=')] opacity-20"></div>
          <div className="container mx-auto px-4 md:px-6 text-center relative z-10">
            <motion.div {...fadeIn}>
              <h2 className="text-4xl md:text-5xl lg:text-6xl font-bold font-heading mb-8 leading-tight">
                Launch Your Dream Today
              </h2>
              <p className="text-xl lg:text-2xl text-blue-500 mb-12 max-w-3xl mx-auto leading-relaxed">
                Our business filing experts can file your paperwork quickly and affordably. 
                Join thousands of successful business owners who started with CorpNet.
              </p>
              <Button 
                size="lg" 
                className="bg-secondary hover:bg-secondary/90 text-white font-bold h-16 px-12 text-xl shadow-2xl hover:shadow-3xl transition-all duration-300 hover:scale-105 border-0"
              >
                Get Started with Your LLC
                <ArrowRight className="ml-2 h-6 w-6" />
              </Button>
            </motion.div>
          </div>
        </section>
      </main>

      {/* {showFooter && <Footer />} */}
    </div>
  );
}
